import { ARCHETYPE_CATALOG, type ArchetypeId, type ColorEmphasis, type ColorMode } from '@mg/shared';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { useDesignPreview } from './useDesignPreview';

/**
 * The five built-in layouts, each previewed at the size actually selected.
 *
 * Not filtered by size, deliberately. Every archetype is a parametric function of the canvas, so all
 * five work at 1080×1920, at 1200×630 and at whatever pixel size someone typed — and filtering would
 * leave a custom size showing an empty panel, which looks like a bug and is the one case where the
 * person most needs to see their options.
 *
 * Previews are the real thing rather than a static picture: same engine, same font metrics, same
 * treatments. Comparing five layouts is only useful if what you compare is what you get.
 */
export function SystemTemplatePanel({
  selected,
  width,
  height,
  emphasis,
  colorMode,
  hasImage,
  variantSeed,
  onPick,
  onBack,
}: {
  selected: ArchetypeId | null;
  width: number;
  height: number;
  emphasis: ColorEmphasis;
  colorMode: ColorMode;
  hasImage: boolean;
  variantSeed: number;
  onPick: (id: ArchetypeId) => void;
  onBack: () => void;
}) {
  // Layouts that need a photo are shown either way, but marked — hiding them would leave someone
  // wondering where the option went, and the fix (choose an image source) is one field away.
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-line px-4 py-2.5">
        <span className="text-sm font-medium text-ink">
          System templates · previewed at {width} × {height}
        </span>
        <Button size="sm" variant="ghost" onClick={onBack}>
          Back to preview
        </Button>
      </div>

      <div
        className="min-h-0 flex-1 overflow-y-auto p-4"
        // Content-sized rows: a grid inside a height-capped flex column compresses its rows to fit
        // instead of scrolling unless told to size to content.
        style={{ gridAutoRows: 'max-content', alignContent: 'start' }}
      >
        <ul className="grid grid-cols-2 gap-3">
          {ARCHETYPE_CATALOG.map((entry) => (
            <li key={entry.id}>
              <ArchetypeCard
                entry={entry}
                selected={selected === entry.id}
                width={width}
                height={height}
                emphasis={emphasis}
                colorMode={colorMode}
                hasImage={hasImage}
                variantSeed={variantSeed}
                onPick={() => onPick(entry.id)}
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function ArchetypeCard({
  entry,
  selected,
  width,
  height,
  emphasis,
  colorMode,
  hasImage,
  variantSeed,
  onPick,
}: {
  entry: (typeof ARCHETYPE_CATALOG)[number];
  selected: boolean;
  width: number;
  height: number;
  emphasis: ColorEmphasis;
  colorMode: ColorMode;
  hasImage: boolean;
  variantSeed: number;
  onPick: () => void;
}) {
  const preview = useDesignPreview({
    archetype: entry.id,
    width,
    height,
    emphasis,
    mode: colorMode,
    // Placeholder copy in the picker, not the user's headline: you are comparing LAYOUTS here, and the
    // same sentence in all five is what makes them comparable.
    hasImage: hasImage || entry.needsImage,
    variantSeed,
  });

  const needsImageButNone = entry.needsImage && !hasImage;

  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={selected}
      className={`focus-ring w-full overflow-hidden rounded-xl border-2 text-left transition-colors ${
        selected ? 'border-action' : 'border-line hover:border-ink-ghost'
      }`}
    >
      <div
        className="flex items-center justify-center bg-page"
        // Match the real canvas shape so a tall layout reads as tall in the grid. Capped so a 9:16
        // preview cannot push the other four off the screen.
        style={{ aspectRatio: `${width} / ${height}`, maxHeight: '22vh' }}
      >
        {preview.url ? (
          <img src={preview.url} alt={entry.name} className="max-h-full max-w-full bg-white object-contain" />
        ) : (
          <span className="text-xs text-ink-faint">{preview.loading ? 'Rendering…' : ''}</span>
        )}
      </div>
      <div className="space-y-1 p-2.5">
        <p className="text-sm font-medium text-ink">{entry.name}</p>
        <p className="text-xs text-ink-muted">{entry.hint}</p>
        {needsImageButNone && (
          // Said before the choice rather than after: this layout has a photo region, and with no image
          // source selected it will render a placeholder box where the picture should be.
          <Badge tone="amber">Needs an image</Badge>
        )}
        {preview.notes.map((note) => (
          <p key={note} className="text-xs text-amber-700 dark:text-warning-dark">
            {note}
          </p>
        ))}
      </div>
    </button>
  );
}
